import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import StorefrontLayout from "components/storefront/StorefrontLayout";
import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { firstMediaUrl, mediaUrl } from "lib/media";
import { currencyForCompany } from "lib/companyProfile";
import { getTextLocale } from "lib/storefront";
import logo from "assets/images/logos/ShortLogo.png";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { storefrontThemeForTenant } from "lib/storefrontTheme";
import { productStoryText } from "storefronts/shared/pimProductContent";

const BRAND = {
  ink: "var(--storefront-ink, #111827)",
  muted: "var(--storefront-muted, #667085)",
  line: "var(--storefront-line, rgba(17,24,39,0.1))",
  canvas: "var(--storefront-canvas, #f7f8fa)",
  red: "var(--storefront-accent, #d7261e)",
  orange: "#f97316",
};

function priceText(value, currency = "USD") {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "Call for pricing";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function resolvePrice(product) {
  const unitPrice = Number(product?.unitPrice);
  if (Number.isFinite(unitPrice) && unitPrice > 0) return product.unitPrice;
  const levels =
    product?.priceLevels && typeof product.priceLevels === "object"
      ? product.priceLevels
      : {};
  return levels.retail ?? levels.Retail ?? levels.RETAIL ?? product?.unitPrice;
}

function priceBadge(product) {
  const tier = String(product?.priceTierApplied || "").trim();
  if (tier && tier.toLowerCase() !== "retail")
    return `${tier.replaceAll("_", " ")} pricing`;
  const scope = String(product?.pricingScope || "").trim();
  if (scope)
    return scope === "customer"
      ? "Customer distributor pricing"
      : "Distributor pricing";
  return "";
}

function normalizeProduct(data) {
  if (!data?.data) return null;
  return data.data.attributes
    ? {
        id: data.data.id,
        documentId: data.data.documentId || data.data.id,
        ...data.data.attributes,
      }
    : { ...data.data, documentId: data.data.documentId || data.data.id };
}

function HtmlOrText({ value, sx }) {
  if (!value) return null;
  if (String(value).includes("<")) {
    return (
      <MDTypography
        variant="body1"
        sx={sx}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  return (
    <MDTypography variant="body1" sx={{ ...sx, whiteSpace: "pre-wrap" }}>
      {value}
    </MDTypography>
  );
}

HtmlOrText.propTypes = {
  value: PropTypes.string,
  sx: PropTypes.shape({}),
};

HtmlOrText.defaultProps = {
  value: "",
  sx: {},
};

export default function ProductDetail() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale } = useStorefrontCopy();
  const { tenant } = useStorefrontTenant();
  const storefrontTheme = storefrontThemeForTenant(tenant, locale);
  const isMobileRoute = location.pathname.startsWith("/m/catalog/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;
  const { addItem, itemCount, lines } = useCart();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const canOrder = !!token;
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [cartNotice, setCartNotice] = useState("");
  const companyKey = String(
    storefrontTenant || activeCompany || "",
  ).toLowerCase();
  const textLocale = getTextLocale();

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "catalog-product",
      documentId,
      companyKey,
      textLocale,
      token ? "auth" : "public",
    ],
    queryFn: () => get(`/api/storefront/catalog/${documentId}`),
    enabled: !!documentId,
    staleTime: 30000,
  });
  const currency =
    data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);

  const product = normalizeProduct(data);
  const story = productStoryText(product);
  const effectivePrice = resolvePrice(product);
  const appliedPriceBadge = priceBadge(product);
  const coreCharge = product?.coreCharge;
  const hasEffectivePrice = Number(effectivePrice) > 0;
  const images = Array.isArray(product?.images?.data)
    ? product.images.data
    : Array.isArray(product?.images)
      ? product.images
      : [];
  const selectedImage = mediaUrl(images[selectedImageIndex]) || null;
  const unitPriceNumber = Number(effectivePrice || 0);
  const coreChargeNumber = Number(coreCharge || 0);
  const lineTotal = hasEffectivePrice
    ? (unitPriceNumber + coreChargeNumber) * orderQuantity
    : 0;
  const inCartQuantity =
    (lines || []).find(
      (line) => String(line.documentId) === String(product?.documentId),
    )?.qty || 0;
  const setQuantity = (value) => {
    const next = Math.max(1, Math.min(99, Math.floor(Number(value || 1) || 1)));
    setOrderQuantity(next);
  };
  const addProductToCart = (goToCart = false) => {
    if (!canOrder) {
      navigate(
        `/m/authentication/sign-in?next=${encodeURIComponent(
          location.pathname,
        )}`,
      );
      return;
    }
    if (!product || !hasEffectivePrice) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: unitPriceNumber,
        coreCharge: coreCharge != null ? coreChargeNumber : null,
        imageUrl: firstMediaUrl(product?.images),
      },
      orderQuantity,
    );
    setCartNotice(`${orderQuantity} added to cart`);
    if (goToCart) navigate(toPath("/cart"));
  };

  if (isLoading) {
    return (
      <StorefrontLayout maxWidth={null}>
        <Container maxWidth="lg" sx={{ py: 10, minHeight: "70vh" }}>
          <MDTypography sx={{ color: BRAND.muted }}>
            Loading product details...
          </MDTypography>
        </Container>
      </StorefrontLayout>
    );
  }

  if (error || !product) {
    return (
      <StorefrontLayout maxWidth={null}>
        <Container
          maxWidth="lg"
          sx={{ py: 10, minHeight: "70vh", textAlign: "center" }}
        >
          <MDTypography
            variant="h4"
            sx={{ color: BRAND.ink, fontWeight: 900, mb: 2 }}
          >
            Product not found
          </MDTypography>
          <MDButton
            variant="contained"
            sx={{
              bgcolor: BRAND.red,
              color: "#fff",
              borderRadius: "999px",
              boxShadow: "none",
            }}
            onClick={() => navigate(toPath("/catalog"))}
          >
            Back to catalog
          </MDButton>
        </Container>
      </StorefrontLayout>
    );
  }

  if (isMobileRoute) {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: "#fbfbfa",
            pb: hasEffectivePrice && canOrder ? 11 : 3,
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              bgcolor: "rgba(251,251,250,0.94)",
              backdropFilter: "blur(18px)",
              borderBottom: `1px solid ${BRAND.line}`,
            }}
          >
            <Container maxWidth="sm" sx={{ px: 2, py: 1 }}>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1.25}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="vbspecialties.com"
                  sx={{ width: 78, height: "auto", display: "block" }}
                  onClick={() => navigate("/m")}
                />
                <Stack direction="row" spacing={0.75}>
                  {[
                    [
                      "arrow_back",
                      () => navigate(toPath("/catalog")),
                      "Catalog",
                    ],
                    ...(canOrder
                      ? [
                          [
                            "shopping_cart",
                            () => navigate(toPath("/cart")),
                            "Cart",
                          ],
                        ]
                      : []),
                    [
                      token ? "person" : "login",
                      () =>
                        navigate(
                          token
                            ? "/m/account"
                            : `/m/authentication/sign-in?next=${encodeURIComponent(
                                location.pathname,
                              )}`,
                        ),
                      token ? "Account" : "Sign in",
                    ],
                  ].map(([icon, onClick, label]) => (
                    <MDButton
                      key={label}
                      variant="outlined"
                      sx={{
                        minWidth: 44,
                        width: 44,
                        height: 44,
                        p: 0,
                        borderRadius: 1.25,
                        bgcolor:
                          label === "Account" || label === "Sign in"
                            ? BRAND.ink
                            : "#fff",
                        color:
                          label === "Account" || label === "Sign in"
                            ? "#fff"
                            : BRAND.ink,
                        borderColor:
                          label === "Account" || label === "Sign in"
                            ? BRAND.ink
                            : BRAND.line,
                        boxShadow: "none",
                      }}
                      onClick={onClick}
                      aria-label={label}
                    >
                      {label === "Cart" ? (
                        <Badge
                          badgeContent={itemCount || 0}
                          color="error"
                          invisible={!itemCount}
                        >
                          <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
                        </Badge>
                      ) : (
                        <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
                      )}
                    </MDButton>
                  ))}
                </Stack>
              </MDBox>
            </Container>
          </Box>

          <Container maxWidth="sm" sx={{ px: 2, pt: 1.5 }}>
            <Box
              sx={{
                bgcolor: "#f0f1f2",
                border: `1px solid ${BRAND.line}`,
                aspectRatio: "4 / 3",
                display: "grid",
                placeItems: "center",
              }}
            >
              {selectedImage ? (
                <Box
                  component="img"
                  src={selectedImage}
                  alt={product.name}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    p: 1.25,
                  }}
                />
              ) : (
                <Icon sx={{ fontSize: 72, color: "#98a2b3" }}>
                  precision_manufacturing
                </Icon>
              )}
            </Box>

            {images.length > 1 ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ py: 1, overflowX: "auto" }}
              >
                {images.map((img, index) => {
                  const imgUrl = mediaUrl(img);
                  const active = index === selectedImageIndex;
                  return (
                    <Box
                      key={index}
                      component="button"
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      sx={{
                        width: 68,
                        height: 68,
                        flexShrink: 0,
                        border: active
                          ? `2px solid ${BRAND.red}`
                          : `1px solid ${BRAND.line}`,
                        bgcolor: "#fff",
                        p: 0,
                      }}
                    >
                      {imgUrl ? (
                        <Box
                          component="img"
                          src={imgUrl}
                          alt={`${product.name} ${index + 1}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            ) : null}

            <Box sx={{ py: 2.25, borderBottom: `1px solid ${BRAND.line}` }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                mb={1}
              >
                <MDTypography
                  variant="caption"
                  sx={{
                    color: BRAND.muted,
                    fontFamily: "monospace",
                    fontWeight: 900,
                  }}
                >
                  {product.sku || "Pending"}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{
                    color:
                      product.status === "available" ? "#147a5c" : BRAND.muted,
                    fontWeight: 900,
                  }}
                >
                  {product.status === "available"
                    ? "READY"
                    : String(product.status || "CATALOG").toUpperCase()}
                </MDTypography>
              </Stack>
              <MDTypography
                variant="h1"
                sx={{
                  color: BRAND.ink,
                  fontWeight: 900,
                  fontSize: "1.75rem",
                  lineHeight: 1.06,
                  letterSpacing: 0,
                }}
              >
                {product.name}
              </MDTypography>
              {story ? (
                <HtmlOrText
                  value={story}
                  sx={{
                    color: BRAND.muted,
                    lineHeight: 1.55,
                    fontSize: "0.92rem",
                    mt: 1.25,
                  }}
                />
              ) : null}
            </Box>

            <Box sx={{ py: 2.25, borderBottom: `1px solid ${BRAND.line}` }}>
              {!canOrder ? (
                <Box
                  sx={{
                    borderTop: `1px solid ${BRAND.line}`,
                    borderBottom: `1px solid ${BRAND.line}`,
                    py: 1.25,
                    mb: 1.5,
                  }}
                >
                  <MDTypography
                    variant="body2"
                    sx={{ color: BRAND.ink, fontWeight: 900 }}
                  >
                    Retail pricing shown for your region.
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: BRAND.muted, fontWeight: 700 }}
                  >
                    Sign in to order and use cart checkout.
                  </MDTypography>
                </Box>
              ) : null}
              {cartNotice ? (
                <Alert
                  severity="success"
                  sx={{ mb: 1.5, borderRadius: 1 }}
                  onClose={() => setCartNotice("")}
                >
                  {cartNotice}
                </Alert>
              ) : null}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                gap={2}
              >
                <Box>
                  <MDTypography
                    variant="caption"
                    sx={{ color: BRAND.muted, fontWeight: 900 }}
                  >
                    PRICE
                  </MDTypography>
                  <MDTypography
                    variant="h3"
                    sx={{
                      color: hasEffectivePrice ? BRAND.red : BRAND.ink,
                      fontWeight: 900,
                      lineHeight: 1.05,
                      fontSize: "1.85rem",
                    }}
                  >
                    {priceText(effectivePrice, currency)}
                  </MDTypography>
                </Box>
                {coreCharge ? (
                  <Box sx={{ textAlign: "right" }}>
                    <MDTypography
                      variant="caption"
                      sx={{ color: BRAND.muted, fontWeight: 900 }}
                    >
                      CORE
                    </MDTypography>
                    <MDTypography
                      variant="button"
                      sx={{
                        color: BRAND.ink,
                        fontWeight: 900,
                        display: "block",
                      }}
                    >
                      {priceText(coreCharge, currency)}
                    </MDTypography>
                  </Box>
                ) : null}
              </Stack>
              {appliedPriceBadge ? (
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "#147a5c",
                    fontWeight: 900,
                    display: "block",
                    mt: 0.75,
                  }}
                >
                  {appliedPriceBadge}
                </MDTypography>
              ) : null}
              {hasEffectivePrice && canOrder ? (
                <Box sx={{ mt: 2, border: `1px solid ${BRAND.line}` }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 1.25, borderBottom: `1px solid ${BRAND.line}` }}
                  >
                    <Box>
                      <MDTypography
                        variant="caption"
                        sx={{ color: BRAND.muted, fontWeight: 900 }}
                      >
                        QTY
                      </MDTypography>
                      {inCartQuantity > 0 ? (
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: BRAND.red,
                            fontWeight: 900,
                            display: "block",
                          }}
                        >
                          {inCartQuantity} already in cart
                        </MDTypography>
                      ) : null}
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0}>
                      <MDButton
                        variant="text"
                        sx={{
                          minWidth: 42,
                          height: 42,
                          borderRadius: 0.75,
                          color: BRAND.ink,
                        }}
                        onClick={() => setQuantity(orderQuantity - 1)}
                      >
                        <Icon>remove</Icon>
                      </MDButton>
                      <TextField
                        type="number"
                        value={orderQuantity}
                        onChange={(event) => setQuantity(event.target.value)}
                        inputProps={{
                          min: 1,
                          max: 99,
                          step: 1,
                          inputMode: "numeric",
                          style: { textAlign: "center" },
                        }}
                        sx={{
                          width: 64,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 0.75,
                            height: 42,
                          },
                          "& input": { py: 0.75, fontWeight: 900 },
                        }}
                      />
                      <MDButton
                        variant="text"
                        sx={{
                          minWidth: 42,
                          height: 42,
                          borderRadius: 0.75,
                          color: BRAND.ink,
                        }}
                        onClick={() => setQuantity(orderQuantity + 1)}
                      >
                        <Icon>add</Icon>
                      </MDButton>
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ p: 1.25 }}
                  >
                    <MDTypography
                      variant="body2"
                      sx={{ color: BRAND.muted, fontWeight: 800 }}
                    >
                      Line total
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      sx={{ color: BRAND.ink, fontWeight: 900 }}
                    >
                      {priceText(lineTotal, currency)}
                    </MDTypography>
                  </Stack>
                </Box>
              ) : hasEffectivePrice ? (
                <MDButton
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    bgcolor: BRAND.ink,
                    color: "#fff",
                    borderRadius: 1,
                    minHeight: 44,
                    fontWeight: 900,
                    boxShadow: "none",
                  }}
                  onClick={() =>
                    navigate(
                      `/m/authentication/sign-in?next=${encodeURIComponent(
                        location.pathname,
                      )}`,
                    )
                  }
                >
                  Sign in to order
                </MDButton>
              ) : null}
            </Box>

            {product.specifications &&
            Object.keys(product.specifications).length > 0 ? (
              <Box sx={{ py: 2.25, borderBottom: `1px solid ${BRAND.line}` }}>
                <MDTypography
                  variant="h6"
                  sx={{ color: BRAND.ink, fontWeight: 900, mb: 1.25 }}
                >
                  Specifications
                </MDTypography>
                {Object.entries(product.specifications).map(
                  ([key, value], index) => (
                    <Box key={key}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        gap={2}
                        py={1}
                      >
                        <MDTypography
                          variant="body2"
                          sx={{ color: BRAND.muted, fontWeight: 800 }}
                        >
                          {key}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          sx={{
                            color: BRAND.ink,
                            fontWeight: 900,
                            textAlign: "right",
                          }}
                        >
                          {String(value)}
                        </MDTypography>
                      </MDBox>
                      {index <
                      Object.keys(product.specifications).length - 1 ? (
                        <Divider />
                      ) : null}
                    </Box>
                  ),
                )}
              </Box>
            ) : null}
          </Container>

          {hasEffectivePrice && canOrder ? (
            <Box
              sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                bgcolor: "#fff",
                borderTop: `1px solid ${BRAND.line}`,
                px: 2,
                pt: 1.1,
                pb: "calc(10px + env(safe-area-inset-bottom))",
              }}
            >
              <Container maxWidth="sm" sx={{ px: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: BRAND.muted,
                        fontWeight: 900,
                        lineHeight: 1.1,
                      }}
                    >
                      {orderQuantity} item{orderQuantity === 1 ? "" : "s"} total
                    </MDTypography>
                    <MDTypography
                      variant="h6"
                      sx={{
                        color: BRAND.ink,
                        fontWeight: 900,
                        lineHeight: 1.2,
                      }}
                    >
                      {priceText(lineTotal, currency)}
                    </MDTypography>
                  </Box>
                  <MDButton
                    variant="outlined"
                    sx={{
                      minWidth: 46,
                      width: 46,
                      height: 46,
                      p: 0,
                      borderRadius: 1,
                      color: BRAND.ink,
                      borderColor: BRAND.line,
                    }}
                    onClick={() => navigate(toPath("/cart"))}
                  >
                    <Icon>shopping_cart</Icon>
                  </MDButton>
                  <MDButton
                    variant="contained"
                    sx={{
                      bgcolor: BRAND.red,
                      color: "#fff",
                      minWidth: 132,
                      height: 46,
                      borderRadius: 1,
                      fontWeight: 900,
                      boxShadow: "none",
                    }}
                    onClick={() => addProductToCart(false)}
                  >
                    Add
                  </MDButton>
                </Stack>
              </Container>
            </Box>
          ) : null}
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "industrial") {
    const specEntries = Object.entries(product.specifications || {}).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    );
    const identityRows = [
      ["SKU", product.sku],
      ["Transmission", product.transmission],
      ["Category", product.category],
      ["Status", product.status],
    ].filter(([, value]) => value);
    const technicalSections = Array.isArray(product?.technicalSections?.data)
      ? product.technicalSections.data.map((section) =>
          section?.attributes ? { ...section.attributes } : section,
        )
      : Array.isArray(product?.technicalSections)
        ? product.technicalSections
        : [];

    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor:
              "radial-gradient(circle at 84% 6%, rgba(181,71,8,0.16), transparent 32%), linear-gradient(180deg, #080B0E 0%, #10151B 48%, #080B0E 100%)",
            color: "#fff",
            py: { xs: 3, md: 4 },
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={{ xs: 2, lg: 3 }} alignItems="stretch">
              <Grid item xs={12} lg={2.2}>
                <Box
                  sx={{
                    position: { lg: "sticky" },
                    top: { lg: 28 },
                    minHeight: { lg: "calc(100vh - 56px)" },
                    display: "grid",
                    gridTemplateRows: "auto 1fr auto",
                    py: { xs: 0, lg: 1 },
                  }}
                >
                  <Box>
                    <MDButton
                      variant="text"
                      startIcon={<Icon>arrow_back</Icon>}
                      onClick={() => navigate(toPath("/catalog"))}
                      sx={{
                        color: "rgba(255,255,255,0.72)",
                        px: 0,
                        borderRadius: 0,
                        fontWeight: 900,
                        "&:hover": {
                          color: "#fff",
                          bgcolor: "transparent",
                        },
                      }}
                    >
                      Catalog
                    </MDButton>
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "rgba(255,255,255,0.48)",
                        fontFamily: "monospace",
                        fontWeight: 900,
                        mt: 3,
                      }}
                    >
                      PRODUCT FILE
                    </MDTypography>
                    <MDTypography
                      variant="h2"
                      sx={{
                        color: "#fff",
                        fontWeight: 950,
                        lineHeight: 0.98,
                        mt: 1,
                        fontSize: { xs: "2.1rem", lg: "2.55rem" },
                        letterSpacing: 0,
                      }}
                    >
                      {product.name}
                    </MDTypography>
                    <Stack spacing={1.1} sx={{ mt: 3 }}>
                      {identityRows.map(([key, value]) => (
                        <Box
                          key={key}
                          sx={{
                            py: 1,
                            borderTop: "1px solid rgba(255,255,255,0.12)",
                          }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "rgba(255,255,255,0.44)",
                              fontFamily: "monospace",
                              fontWeight: 900,
                            }}
                          >
                            {key}
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            sx={{ color: "#fff", fontWeight: 900, mt: 0.35 }}
                          >
                            {String(value)}
                          </MDTypography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Box sx={{ display: { xs: "none", lg: "block" } }} />

                  <Box sx={{ mt: { xs: 2, lg: 0 } }}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.48)",
                        fontWeight: 900,
                        fontFamily: "monospace",
                      }}
                    >
                      CURRENT PRICE
                    </MDTypography>
                    <MDTypography
                      variant="h3"
                      sx={{ color: storefrontTheme.accent, fontWeight: 950 }}
                    >
                      {priceText(effectivePrice, currency)}
                    </MDTypography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} lg={5.8}>
                <Box
                  sx={{
                    minHeight: { xs: 390, lg: "calc(100vh - 72px)" },
                    display: "grid",
                    gridTemplateRows: "1fr auto",
                    bgcolor:
                      "linear-gradient(145deg, rgba(255,255,255,0.09), rgba(255,255,255,0.02))",
                    boxShadow: "0 34px 110px rgba(0,0,0,0.42)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      p: { xs: 2.25, md: 4 },
                      backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
                      backgroundSize: "28px 28px",
                    }}
                  >
                    {selectedImage ? (
                      <Box
                        component="img"
                        src={selectedImage}
                        alt={product.name}
                        sx={{
                          width: "100%",
                          maxHeight: { xs: 360, lg: 650 },
                          objectFit: "contain",
                          filter: "drop-shadow(0 34px 56px rgba(0,0,0,0.38))",
                        }}
                      />
                    ) : (
                      <Icon sx={{ fontSize: 112, color: "rgba(255,255,255,0.24)" }}>
                        precision_manufacturing
                      </Icon>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{
                      px: 1,
                      py: 1,
                      bgcolor: "rgba(0,0,0,0.24)",
                      overflowX: "auto",
                    }}
                  >
                    {(images.length ? images : [null]).map((img, index) => {
                      const imgUrl = mediaUrl(img);
                      const active = index === selectedImageIndex;
                      return (
                        <Box
                          key={index}
                          component="button"
                          type="button"
                          onClick={() => setSelectedImageIndex(index)}
                          sx={{
                            width: 98,
                            height: 74,
                            flexShrink: 0,
                            p: 0,
                            bgcolor: active
                              ? "rgba(181,71,8,0.22)"
                              : "rgba(255,255,255,0.08)",
                            border: 0,
                            borderBottom: active
                              ? `3px solid ${storefrontTheme.accent}`
                              : "3px solid transparent",
                            cursor: "pointer",
                          }}
                        >
                          {imgUrl ? (
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`${product.name} ${index + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                p: 0.5,
                              }}
                            />
                          ) : (
                            <Icon sx={{ color: "rgba(255,255,255,0.5)" }}>
                              inventory_2
                            </Icon>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      bgcolor: "#F7F8F5",
                      color: "#0B0E12",
                      p: { xs: 2.25, md: 3 },
                      boxShadow: "0 34px 90px rgba(0,0,0,0.24)",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "#65717D",
                        fontFamily: "monospace",
                        fontWeight: 900,
                      }}
                    >
                      ORDER CONTROL
                    </MDTypography>
                    <Stack direction="row" alignItems="flex-end" gap={2} mt={1}>
                      <Box sx={{ flex: 1 }}>
                        <MDTypography
                          variant="h2"
                          sx={{
                            color: storefrontTheme.accent,
                            fontWeight: 950,
                            lineHeight: 0.96,
                          }}
                        >
                          {priceText(effectivePrice, currency)}
                        </MDTypography>
                        {appliedPriceBadge ? (
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: "#147A5C",
                              display: "block",
                              fontWeight: 900,
                              mt: 0.75,
                            }}
                          >
                            {appliedPriceBadge}
                          </MDTypography>
                        ) : null}
                      </Box>
                      {coreCharge ? (
                        <Box sx={{ minWidth: 120 }}>
                          <MDTypography
                            variant="caption"
                            sx={{ color: "#65717D", fontWeight: 900 }}
                          >
                            CORE
                          </MDTypography>
                          <MDTypography
                            variant="h6"
                            sx={{ color: "#0B0E12", fontWeight: 950 }}
                          >
                            {priceText(coreCharge, currency)}
                          </MDTypography>
                        </Box>
                      ) : null}
                    </Stack>
                    <Divider sx={{ my: 2.25, borderColor: "rgba(11,14,18,0.16)" }} />
                    {hasEffectivePrice && canOrder ? (
                      <Stack spacing={1.25}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.25}
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          <TextField
                            variant="standard"
                            label="Quantity"
                            type="number"
                            value={orderQuantity}
                            onChange={(event) => setQuantity(event.target.value)}
                            inputProps={{ min: 1, max: 99, step: 1 }}
                            sx={{
                              width: { xs: "100%", sm: 120 },
                              "& .MuiInputBase-input": { fontWeight: 950 },
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <MDTypography
                              variant="caption"
                              sx={{ color: "#65717D", fontWeight: 900 }}
                            >
                              LINE TOTAL
                            </MDTypography>
                            <MDTypography
                              variant="h5"
                              sx={{ color: "#0B0E12", fontWeight: 950 }}
                            >
                              {priceText(lineTotal, currency)}
                            </MDTypography>
                          </Box>
                        </Stack>
                        <MDButton
                          variant="contained"
                          sx={{
                            bgcolor: "#0B0E12",
                            color: "#fff",
                            borderRadius: 0,
                            minHeight: 50,
                            fontWeight: 950,
                            boxShadow: "none",
                            "&:hover": { bgcolor: storefrontTheme.accent },
                          }}
                          onClick={() => addProductToCart(false)}
                        >
                          {t("product.addToCart", "Add to cart")}
                        </MDButton>
                        <MDButton
                          variant="text"
                          sx={{
                            color: "#0B0E12",
                            borderRadius: 0,
                            fontWeight: 950,
                            borderBottom: "2px solid rgba(11,14,18,0.2)",
                          }}
                          onClick={() => addProductToCart(true)}
                        >
                          Add and review cart
                        </MDButton>
                      </Stack>
                    ) : (
                      <MDButton
                        variant="contained"
                        sx={{
                          bgcolor: "#0B0E12",
                          color: "#fff",
                          borderRadius: 0,
                          minHeight: 50,
                          fontWeight: 950,
                          boxShadow: "none",
                        }}
                        onClick={() =>
                          navigate(
                            `/authentication/sign-in?next=${encodeURIComponent(
                              location.pathname,
                            )}`,
                          )
                        }
                      >
                        Sign in to order
                      </MDButton>
                    )}
                    {cartNotice ? (
                      <Alert
                        severity="success"
                        sx={{ mt: 1.5, borderRadius: 0 }}
                        onClose={() => setCartNotice("")}
                      >
                        {cartNotice}
                      </Alert>
                    ) : null}
                  </Box>

                  <Box sx={{ color: "#fff" }}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.48)",
                        fontFamily: "monospace",
                        fontWeight: 900,
                      }}
                    >
                      SPECIFICATION INDEX
                    </MDTypography>
                    <Stack spacing={0} sx={{ mt: 1.25 }}>
                      {(specEntries.length ? specEntries : identityRows).slice(0, 10).map(
                        ([key, value]) => (
                          <Stack
                            key={key}
                            direction="row"
                            justifyContent="space-between"
                            gap={2}
                            sx={{
                              py: 1.15,
                              borderTop: "1px solid rgba(255,255,255,0.12)",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{ color: "rgba(255,255,255,0.48)", fontWeight: 900 }}
                            >
                              {key}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{
                                color: "#fff",
                                fontWeight: 950,
                                textAlign: "right",
                              }}
                            >
                              {String(value)}
                            </MDTypography>
                          </Stack>
                        ),
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Grid container spacing={{ xs: 2, lg: 3 }} sx={{ mt: 3 }}>
              <Grid item xs={12} lg={7}>
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.06)",
                    color: "#fff",
                    p: { xs: 2.25, md: 3 },
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.48)",
                      fontFamily: "monospace",
                      fontWeight: 900,
                    }}
                  >
                    TECHNICAL OVERVIEW
                  </MDTypography>
                  <MDTypography
                    variant="h3"
                    sx={{ color: "#fff", fontWeight: 950, mt: 0.5, mb: 1.5 }}
                  >
                    Product notes
                  </MDTypography>
                  <HtmlOrText
                    value={product.unitSpecificDetails || story}
                    sx={{
                      color: "rgba(255,255,255,0.76)",
                      lineHeight: 1.8,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Box
                  sx={{
                    bgcolor: "#F7F8F5",
                    color: "#0B0E12",
                    p: { xs: 2.25, md: 3 },
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "#65717D",
                      fontFamily: "monospace",
                      fontWeight: 900,
                    }}
                  >
                    TECHNICAL DATA
                  </MDTypography>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {technicalSections.length ? (
                      technicalSections.slice(0, 4).map((section, index) => (
                        <Box
                          key={`${section?.title || "section"}-${index}`}
                          sx={{ borderTop: "1px solid rgba(11,14,18,0.14)", pt: 1.5 }}
                        >
                          <MDTypography
                            variant="body2"
                            sx={{ color: "#0B0E12", fontWeight: 950 }}
                          >
                            {section?.title || `Technical note ${index + 1}`}
                          </MDTypography>
                          {section?.body ? (
                            <HtmlOrText
                              value={section.body}
                              sx={{ color: "#65717D", lineHeight: 1.6, mt: 0.5 }}
                            />
                          ) : null}
                        </Box>
                      ))
                    ) : (
                      identityRows.map(([key, value]) => (
                        <Stack
                          key={key}
                          direction="row"
                          justifyContent="space-between"
                          sx={{ borderTop: "1px solid rgba(11,14,18,0.14)", pt: 1.2 }}
                        >
                          <MDTypography
                            variant="body2"
                            sx={{ color: "#65717D", fontWeight: 900 }}
                          >
                            {key}
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            sx={{ color: "#0B0E12", fontWeight: 950 }}
                          >
                            {String(value)}
                          </MDTypography>
                        </Stack>
                      ))
                    )}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "commerce") {
    const specEntries = Object.entries(product.specifications || {}).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    );
    const featureRows = [
      ["SKU", product.sku],
      ["Status", product.status],
      ["Transmission", product.transmission],
      ["Category", product.category],
    ].filter(([, value]) => value);

    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor:
              "linear-gradient(180deg, #FFFFFF 0%, #F7F9FC 42%, #EEF4F8 100%)",
            py: { xs: 3, md: 5 },
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={{ xs: 3, lg: 5 }} alignItems="flex-start">
              <Grid item xs={12} lg={7}>
                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                  <MDButton
                    variant="text"
                    startIcon={<Icon>arrow_back</Icon>}
                    onClick={() => navigate(toPath("/catalog"))}
                    sx={{
                      color: BRAND.muted,
                      borderRadius: 0,
                      px: 0,
                      fontWeight: 900,
                      "&:hover": { bgcolor: "transparent", color: BRAND.ink },
                    }}
                  >
                    Back to products
                  </MDButton>
                  <MDTypography
                    variant="h1"
                    sx={{
                      color: BRAND.ink,
                      fontWeight: 950,
                      fontSize: { xs: "2.7rem", md: "5.2rem" },
                      lineHeight: 0.94,
                      letterSpacing: 0,
                      maxWidth: 860,
                      mt: 1.5,
                    }}
                  >
                    {product.name}
                  </MDTypography>
                  <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    {featureRows.slice(0, 3).map(([key, value]) => (
                      <MDTypography
                        key={key}
                        variant="caption"
                        sx={{
                          color: BRAND.muted,
                          fontWeight: 900,
                          borderBottom: `2px solid ${storefrontTheme.accent}`,
                          pb: 0.35,
                        }}
                      >
                        {key}: {String(value)}
                      </MDTypography>
                    ))}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 138px" },
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      minHeight: { xs: 420, md: 680 },
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "#FFFFFF",
                      boxShadow: "0 36px 100px rgba(16,24,40,0.12)",
                      p: { xs: 2, md: 4 },
                    }}
                  >
                    {selectedImage ? (
                      <Box
                        component="img"
                        src={selectedImage}
                        alt={product.name}
                        sx={{
                          width: "100%",
                          maxHeight: 660,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Icon sx={{ fontSize: 118, color: "#98A2B3" }}>
                        inventory_2
                      </Icon>
                    )}
                  </Box>
                  <Stack
                    spacing={1.25}
                    sx={{ overflowX: { xs: "auto", md: "visible" }, flexDirection: { xs: "row", md: "column" } }}
                  >
                    {(images.length ? images : [null]).map((img, index) => {
                      const imgUrl = mediaUrl(img);
                      const active = index === selectedImageIndex;
                      return (
                        <Box
                          key={index}
                          component="button"
                          type="button"
                          onClick={() => setSelectedImageIndex(index)}
                          sx={{
                            width: { xs: 106, md: "100%" },
                            height: { xs: 86, md: 128 },
                            flexShrink: 0,
                            border: 0,
                            bgcolor: active ? "#101828" : "#FFFFFF",
                            boxShadow: active
                              ? "0 20px 45px rgba(16,24,40,0.18)"
                              : "0 12px 34px rgba(16,24,40,0.07)",
                            cursor: "pointer",
                            p: 1,
                          }}
                        >
                          {imgUrl ? (
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`${product.name} ${index + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <Icon sx={{ color: active ? "#fff" : BRAND.muted }}>
                              image
                            </Icon>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Box sx={{ position: { lg: "sticky" }, top: 96 }}>
                  <Box
                    sx={{
                      bgcolor: "#FFFFFF",
                      p: { xs: 2.5, md: 3.5 },
                      boxShadow: "0 32px 90px rgba(16,24,40,0.12)",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{ color: BRAND.muted, fontWeight: 900 }}
                    >
                      Shopping summary
                    </MDTypography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "flex-end" }}
                      gap={2}
                      sx={{ mt: 0.75 }}
                    >
                      <Box>
                        <MDTypography
                          variant="h2"
                          sx={{ color: BRAND.red, fontWeight: 950, lineHeight: 0.98 }}
                        >
                          {priceText(effectivePrice, currency)}
                        </MDTypography>
                        {appliedPriceBadge ? (
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: "#147A5C",
                              display: "block",
                              fontWeight: 900,
                              mt: 0.75,
                            }}
                          >
                            {appliedPriceBadge}
                          </MDTypography>
                        ) : null}
                      </Box>
                      {coreCharge ? (
                        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                          <MDTypography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 900 }}
                          >
                            Refundable core
                          </MDTypography>
                          <MDTypography
                            variant="h6"
                            sx={{ color: BRAND.ink, fontWeight: 950 }}
                          >
                            {priceText(coreCharge, currency)}
                          </MDTypography>
                        </Box>
                      ) : null}
                    </Stack>

                    <Divider sx={{ my: 2.5, borderColor: "rgba(16,24,40,0.10)" }} />

                    {hasEffectivePrice && canOrder ? (
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={{ xs: "stretch", sm: "center" }}
                          spacing={2}
                        >
                          <TextField
                            variant="standard"
                            label="Quantity"
                            type="number"
                            value={orderQuantity}
                            onChange={(event) => setQuantity(event.target.value)}
                            inputProps={{ min: 1, max: 99, step: 1 }}
                            sx={{ width: { xs: "100%", sm: 132 } }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <MDTypography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 900 }}
                            >
                              Order total
                            </MDTypography>
                            <MDTypography
                              variant="h5"
                              sx={{ color: BRAND.ink, fontWeight: 950 }}
                            >
                              {priceText(lineTotal, currency)}
                            </MDTypography>
                          </Box>
                        </Stack>
                        <MDButton
                          variant="contained"
                          sx={{
                            bgcolor: BRAND.red,
                            color: "#fff",
                            borderRadius: 2,
                            minHeight: 54,
                            fontWeight: 950,
                            boxShadow: "none",
                          }}
                          onClick={() => addProductToCart(true)}
                        >
                          {t("product.addToCart", "Add to cart")}
                        </MDButton>
                      </Stack>
                    ) : (
                      <MDButton
                        variant="contained"
                        sx={{
                          bgcolor: BRAND.ink,
                          color: "#fff",
                          borderRadius: 2,
                          minHeight: 54,
                          fontWeight: 950,
                          boxShadow: "none",
                        }}
                        onClick={() =>
                          navigate(
                            `/authentication/sign-in?next=${encodeURIComponent(
                              location.pathname,
                            )}`,
                          )
                        }
                      >
                        Sign in to order
                      </MDButton>
                    )}
                    {cartNotice ? (
                      <Alert
                        severity="success"
                        sx={{ mt: 1.5, borderRadius: 2 }}
                        onClose={() => setCartNotice("")}
                      >
                        {cartNotice}
                      </Alert>
                    ) : null}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <MDTypography
                      variant="h4"
                      sx={{ color: BRAND.ink, fontWeight: 950, mb: 1.25 }}
                    >
                      Product story
                    </MDTypography>
                    <HtmlOrText
                      value={story}
                      sx={{ color: BRAND.muted, lineHeight: 1.85, fontSize: "1.02rem" }}
                    />
                  </Box>

                  {product.unitSpecificDetails ? (
                    <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${BRAND.line}` }}>
                      <MDTypography
                        variant="h5"
                        sx={{ color: BRAND.ink, fontWeight: 950, mb: 1 }}
                      >
                        Unit details
                      </MDTypography>
                      <HtmlOrText
                        value={product.unitSpecificDetails}
                        sx={{ color: BRAND.muted, lineHeight: 1.75 }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Grid>
            </Grid>

            {specEntries.length ? (
              <Box sx={{ mt: { xs: 4, md: 6 } }}>
                <MDTypography
                  variant="h2"
                  sx={{
                    color: BRAND.ink,
                    fontWeight: 950,
                    fontSize: { xs: "2rem", md: "3rem" },
                    mb: 2,
                  }}
                >
                  Specifications
                </MDTypography>
                <Grid container spacing={{ xs: 0, md: 3 }}>
                  {specEntries.map(([key, value]) => (
                    <Grid item xs={12} md={6} key={key}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={2}
                        sx={{
                          py: 1.5,
                          borderTop: `1px solid ${BRAND.line}`,
                        }}
                      >
                        <MDTypography
                          variant="body2"
                          sx={{ color: BRAND.muted, fontWeight: 900 }}
                        >
                          {key}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          sx={{
                            color: BRAND.ink,
                            fontWeight: 950,
                            textAlign: "right",
                          }}
                        >
                          {String(value)}
                        </MDTypography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : null}
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout maxWidth={null}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: isMobileRoute ? "#fbfbfa" : "#fff",
          pb: isMobileRoute && hasEffectivePrice ? 11 : 0,
        }}
      >
        {isMobileRoute ? (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              bgcolor: "rgba(251,251,250,0.92)",
              backdropFilter: "blur(18px)",
              borderBottom: `1px solid ${BRAND.line}`,
            }}
          >
            <Container maxWidth="sm" sx={{ px: 2, py: 1 }}>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1.25}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="vbspecialties.com"
                  sx={{ width: 78, height: "auto", display: "block" }}
                  onClick={() => navigate("/m")}
                />
                <Stack direction="row" spacing={0.75}>
                  {[
                    [
                      "arrow_back",
                      () => navigate(toPath("/catalog")),
                      "Catalog",
                    ],
                    ...(canOrder
                      ? [
                          [
                            "shopping_cart",
                            () => navigate(toPath("/cart")),
                            "Cart",
                          ],
                        ]
                      : []),
                    [
                      token ? "person" : "login",
                      () =>
                        navigate(
                          token
                            ? "/m/account"
                            : `/m/authentication/sign-in?next=${encodeURIComponent(
                                location.pathname,
                              )}`,
                        ),
                      token ? "Account" : "Sign in",
                    ],
                  ].map(([icon, onClick, label]) => (
                    <MDButton
                      key={label}
                      variant="outlined"
                      sx={{
                        minWidth: 44,
                        width: 44,
                        height: 44,
                        p: 0,
                        borderRadius: 1,
                        bgcolor:
                          label === "Account" || label === "Sign in"
                            ? BRAND.ink
                            : "#fff",
                        color:
                          label === "Account" || label === "Sign in"
                            ? "#fff"
                            : BRAND.ink,
                        borderColor:
                          label === "Account" || label === "Sign in"
                            ? BRAND.ink
                            : BRAND.line,
                        boxShadow: "none",
                      }}
                      onClick={onClick}
                      aria-label={label}
                    >
                      {label === "Cart" ? (
                        <Badge
                          badgeContent={itemCount || 0}
                          color="error"
                          invisible={!itemCount}
                        >
                          <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
                        </Badge>
                      ) : (
                        <Icon sx={{ fontSize: 24 }}>{icon}</Icon>
                      )}
                    </MDButton>
                  ))}
                </Stack>
              </MDBox>
            </Container>
          </Box>
        ) : null}
        <Container
          maxWidth="lg"
          sx={{
            py: { xs: isMobileRoute ? 1.5 : 4, md: 6 },
            px: { xs: 2, md: 3 },
          }}
        >
          {!isMobileRoute ? (
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={3}
            >
              <MDButton
                variant="text"
                startIcon={<Icon>arrow_back</Icon>}
                onClick={() => navigate(toPath("/catalog"))}
                sx={{
                  color: BRAND.ink,
                  fontWeight: 900,
                  px: 0,
                  "&:hover": { color: BRAND.red },
                }}
              >
                Catalog
              </MDButton>
            </MDBox>
          ) : null}

          <Grid
            container
            spacing={{ xs: isMobileRoute ? 2 : 4, md: 6 }}
            alignItems="flex-start"
          >
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: isMobileRoute ? 0 : "22px",
                  border: isMobileRoute ? 0 : `1px solid ${BRAND.line}`,
                  borderBottom: isMobileRoute
                    ? `1px solid ${BRAND.line}`
                    : undefined,
                  boxShadow: isMobileRoute
                    ? "none"
                    : "0 24px 70px rgba(17,24,39,0.08)",
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                {selectedImage ? (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: isMobileRoute ? "4 / 3" : "1 / 1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component="img"
                      src={selectedImage}
                      alt={product.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        p: { xs: 1.5, md: 3 },
                      }}
                    />
                  </Box>
                ) : (
                  <MDBox
                    sx={{
                      width: "100%",
                      aspectRatio: isMobileRoute ? "4 / 3" : "1 / 1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 110, color: "#98a2b3" }}>
                      precision_manufacturing
                    </Icon>
                  </MDBox>
                )}
              </Card>

              {images.length > 1 ? (
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {images.map((img, index) => {
                    const imgUrl = mediaUrl(img);
                    const active = index === selectedImageIndex;
                    return (
                      <Grid item xs={3} key={index}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSelectedImageIndex(index)}
                          sx={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            border: active
                              ? `2px solid ${BRAND.red}`
                              : `1px solid ${BRAND.line}`,
                            borderRadius: 0.75,
                            overflow: "hidden",
                            bgcolor: "#fff",
                            p: 0,
                            cursor: "pointer",
                          }}
                        >
                          {imgUrl ? (
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`${product.name} ${index + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <MDBox
                              height="100%"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon sx={{ color: "#98a2b3" }}>image</Icon>
                            </MDBox>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : null}
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox sx={{ position: { md: "sticky" }, top: { md: 96 } }}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={0.8}
                  flexWrap="wrap"
                  mb={isMobileRoute ? 1.25 : 2}
                >
                  <Chip
                    label={
                      product.status === "available"
                        ? "Available"
                        : product.status || "Catalog item"
                    }
                    size="small"
                    sx={{
                      bgcolor:
                        product.status === "available"
                          ? "rgba(34,197,94,0.1)"
                          : "rgba(17,24,39,0.06)",
                      color:
                        product.status === "available"
                          ? "#167b3f"
                          : BRAND.muted,
                      borderRadius: 1,
                      fontWeight: 900,
                    }}
                  />
                  <Chip
                    label={`SKU ${product.sku || "Pending"}`}
                    size="small"
                    sx={{
                      bgcolor: BRAND.canvas,
                      color: BRAND.muted,
                      borderRadius: 1,
                      fontFamily: "monospace",
                    }}
                  />
                  {appliedPriceBadge ? (
                    <Chip
                      label={appliedPriceBadge}
                      size="small"
                      sx={{
                        bgcolor: "rgba(20,122,92,0.1)",
                        color: "#147a5c",
                        borderRadius: 1,
                        fontWeight: 900,
                      }}
                    />
                  ) : null}
                </MDBox>

                <MDTypography
                  variant="h1"
                  sx={{
                    color: BRAND.ink,
                    fontWeight: 900,
                    fontSize: {
                      xs: isMobileRoute ? "1.55rem" : "2.2rem",
                      md: "3.45rem",
                    },
                    lineHeight: isMobileRoute ? 1.1 : 1.02,
                    letterSpacing: 0,
                    mb: isMobileRoute ? 1.25 : 2,
                  }}
                >
                  {product.name}
                </MDTypography>

                {story ? (
                  <HtmlOrText
                    value={story}
                    sx={{
                      color: BRAND.muted,
                      lineHeight: 1.65,
                      fontSize: isMobileRoute ? "0.92rem" : "1rem",
                      mb: isMobileRoute ? 1.75 : 3,
                    }}
                  />
                ) : null}

                <Card
                  sx={{
                    borderRadius: isMobileRoute ? 0 : "18px",
                    border: isMobileRoute ? 0 : `1px solid ${BRAND.line}`,
                    borderTop: isMobileRoute
                      ? `1px solid ${BRAND.line}`
                      : undefined,
                    borderBottom: isMobileRoute
                      ? `1px solid ${BRAND.line}`
                      : undefined,
                    boxShadow: isMobileRoute
                      ? "none"
                      : "0 18px 50px rgba(17,24,39,0.06)",
                    mb: 3,
                    overflow: "hidden",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    {cartNotice ? (
                      <Alert
                        severity="success"
                        sx={{ mb: 2, borderRadius: 1 }}
                        onClose={() => setCartNotice("")}
                      >
                        {cartNotice}
                      </Alert>
                    ) : null}
                    <MDBox
                      display="flex"
                      alignItems="flex-end"
                      justifyContent="space-between"
                      gap={2}
                      mb={2}
                    >
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          sx={{ color: BRAND.muted, fontWeight: 800 }}
                        >
                          Price
                        </MDTypography>
                        <MDTypography
                          variant="h3"
                          sx={{
                            color: hasEffectivePrice ? BRAND.red : BRAND.ink,
                            fontWeight: 900,
                            lineHeight: 1.1,
                            fontSize: hasEffectivePrice
                              ? { xs: "1.65rem", md: "2rem" }
                              : { xs: "1.2rem", md: "1.8rem" },
                          }}
                        >
                          {priceText(effectivePrice, currency)}
                        </MDTypography>
                      </MDBox>
                      {coreCharge ? (
                        <MDBox textAlign="right">
                          <MDTypography
                            variant="caption"
                            sx={{ color: BRAND.muted, fontWeight: 800 }}
                          >
                            Core charge
                          </MDTypography>
                          <MDTypography
                            variant="h6"
                            sx={{ color: BRAND.ink, fontWeight: 900 }}
                          >
                            {priceText(coreCharge, currency)}
                          </MDTypography>
                        </MDBox>
                      ) : null}
                    </MDBox>
                    {coreCharge ? (
                      <MDTypography
                        variant="body2"
                        sx={{ color: BRAND.muted, mb: 2, lineHeight: 1.55 }}
                      >
                        Refundable core deposit. Returned after your rebuildable
                        core is received and processed.
                      </MDTypography>
                    ) : null}
                    {hasEffectivePrice ? (
                      <>
                        {!canOrder ? (
                          <MDBox
                            sx={{
                              borderTop: `1px solid ${BRAND.line}`,
                              borderBottom: `1px solid ${BRAND.line}`,
                              py: 1.25,
                              mb: 1.5,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              sx={{ color: BRAND.ink, fontWeight: 900 }}
                            >
                              Retail pricing shown for your region.
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{ color: BRAND.muted, fontWeight: 700 }}
                            >
                              Sign in to order and use cart checkout.
                            </MDTypography>
                          </MDBox>
                        ) : null}
                        {canOrder ? (
                          <MDBox
                            sx={{
                              border: `1px solid ${BRAND.line}`,
                              borderRadius: 1,
                              p: 1.5,
                              mb: 1.5,
                              bgcolor: "#fff",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              gap={2}
                            >
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  sx={{ color: BRAND.muted, fontWeight: 800 }}
                                >
                                  Quantity
                                </MDTypography>
                                {inCartQuantity > 0 ? (
                                  <MDTypography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      color: BRAND.red,
                                      fontWeight: 900,
                                    }}
                                  >
                                    {inCartQuantity} already in cart
                                  </MDTypography>
                                ) : null}
                              </MDBox>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                              >
                                <MDButton
                                  variant="outlined"
                                  sx={{
                                    minWidth: 42,
                                    width: 42,
                                    height: 42,
                                    p: 0,
                                    borderRadius: 1,
                                    color: BRAND.ink,
                                    borderColor: BRAND.line,
                                  }}
                                  onClick={() => setQuantity(orderQuantity - 1)}
                                >
                                  <Icon>remove</Icon>
                                </MDButton>
                                <TextField
                                  type="number"
                                  value={orderQuantity}
                                  onChange={(event) =>
                                    setQuantity(event.target.value)
                                  }
                                  inputProps={{
                                    min: 1,
                                    max: 99,
                                    step: 1,
                                    inputMode: "numeric",
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{
                                    width: 74,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                      height: 42,
                                    },
                                    "& input": { py: 0.75, fontWeight: 900 },
                                  }}
                                />
                                <MDButton
                                  variant="outlined"
                                  sx={{
                                    minWidth: 42,
                                    width: 42,
                                    height: 42,
                                    p: 0,
                                    borderRadius: 1,
                                    color: BRAND.ink,
                                    borderColor: BRAND.line,
                                  }}
                                  onClick={() => setQuantity(orderQuantity + 1)}
                                >
                                  <Icon>add</Icon>
                                </MDButton>
                              </Stack>
                            </Stack>
                            <Divider sx={{ my: 1.5 }} />
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              gap={2}
                            >
                              <MDTypography
                                variant="body2"
                                sx={{ color: BRAND.muted, fontWeight: 700 }}
                              >
                                Estimated line total
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                sx={{ color: BRAND.ink, fontWeight: 900 }}
                              >
                                {priceText(lineTotal, currency)}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        ) : null}
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <MDButton
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{
                              bgcolor: BRAND.red,
                              color: "#fff",
                              borderRadius: 1,
                              py: 1.45,
                              fontWeight: 900,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: "#b91c1c",
                                boxShadow: "none",
                              },
                            }}
                            onClick={() => addProductToCart(false)}
                          >
                            {canOrder ? "Add to cart" : "Sign in to order"}
                          </MDButton>
                          {canOrder ? (
                            <MDButton
                              variant="outlined"
                              fullWidth
                              size="large"
                              sx={{
                                color: BRAND.ink,
                                borderColor: "rgba(17,24,39,0.16)",
                                borderRadius: 1,
                                py: 1.45,
                                fontWeight: 900,
                                boxShadow: "none",
                                "&:hover": {
                                  borderColor: BRAND.red,
                                  color: BRAND.red,
                                  boxShadow: "none",
                                },
                              }}
                              onClick={() => addProductToCart(true)}
                            >
                              Checkout
                            </MDButton>
                          ) : null}
                        </Stack>
                      </>
                    ) : (
                      <MDButton
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          bgcolor: BRAND.ink,
                          color: "#fff",
                          borderRadius: 1,
                          py: 1.45,
                          fontWeight: 900,
                          boxShadow: "none",
                          "&:hover": { bgcolor: BRAND.red, boxShadow: "none" },
                        }}
                        onClick={() => navigate(toPath("/catalog"))}
                      >
                        Call for pricing
                      </MDButton>
                    )}
                    {canOrder && (hasEffectivePrice || itemCount) ? (
                      <MDButton
                        variant="text"
                        fullWidth
                        sx={{
                          color: BRAND.ink,
                          fontWeight: 900,
                          mt: 1,
                          "&:hover": { color: BRAND.red },
                        }}
                        onClick={() => navigate(toPath("/cart"))}
                      >
                        View cart {itemCount ? `(${itemCount})` : ""}
                      </MDButton>
                    ) : null}
                  </CardContent>
                </Card>

                {product.specifications &&
                Object.keys(product.specifications).length > 0 ? (
                  <Card
                    sx={{
                      borderRadius: isMobileRoute ? 0 : "18px",
                      border: isMobileRoute ? 0 : `1px solid ${BRAND.line}`,
                      borderTop: isMobileRoute
                        ? `1px solid ${BRAND.line}`
                        : undefined,
                      borderBottom: isMobileRoute
                        ? `1px solid ${BRAND.line}`
                        : undefined,
                      boxShadow: "none",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <MDTypography
                        variant="h6"
                        sx={{ color: BRAND.ink, fontWeight: 900, mb: 2 }}
                      >
                        Specifications
                      </MDTypography>
                      {Object.entries(product.specifications).map(
                        ([key, value], index) => (
                          <Box key={key}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              gap={2}
                              py={1.1}
                            >
                              <MDTypography
                                variant="body2"
                                sx={{ color: BRAND.muted, fontWeight: 700 }}
                              >
                                {key}
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                sx={{
                                  color: BRAND.ink,
                                  fontWeight: 800,
                                  textAlign: "right",
                                }}
                              >
                                {String(value)}
                              </MDTypography>
                            </MDBox>
                            {index <
                            Object.keys(product.specifications).length - 1 ? (
                              <Divider />
                            ) : null}
                          </Box>
                        ),
                      )}
                    </CardContent>
                  </Card>
                ) : null}
              </MDBox>
            </Grid>
          </Grid>

          {product.unitSpecificDetails ? (
            <Card
              sx={{
                mt: 4,
                borderRadius: isMobileRoute ? 0 : "18px",
                border: isMobileRoute ? 0 : `1px solid ${BRAND.line}`,
                borderTop: isMobileRoute
                  ? `1px solid ${BRAND.line}`
                  : undefined,
                borderBottom: isMobileRoute
                  ? `1px solid ${BRAND.line}`
                  : undefined,
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <MDTypography
                  variant="h5"
                  sx={{ color: BRAND.ink, fontWeight: 900, mb: 2 }}
                >
                  Unit details
                </MDTypography>
                <HtmlOrText
                  value={product.unitSpecificDetails}
                  sx={{
                    color: BRAND.muted,
                    lineHeight: 1.8,
                    fontSize: "0.98rem",
                  }}
                />
              </CardContent>
            </Card>
          ) : null}

          {Array.isArray(product.technicalSections) &&
          product.technicalSections.length > 0 ? (
            <Card
              sx={{
                mt: 4,
                borderRadius: isMobileRoute ? 0 : "18px",
                border: isMobileRoute ? 0 : `1px solid ${BRAND.line}`,
                borderTop: isMobileRoute
                  ? `1px solid ${BRAND.line}`
                  : undefined,
                borderBottom: isMobileRoute
                  ? `1px solid ${BRAND.line}`
                  : undefined,
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <MDTypography
                  variant="h5"
                  sx={{ color: BRAND.ink, fontWeight: 900, mb: 2 }}
                >
                  Product details
                </MDTypography>
                <Grid container spacing={2}>
                  {product.technicalSections.map((section, index) => (
                    <Grid item xs={12} key={index}>
                      <MDBox
                        sx={{
                          bgcolor: BRAND.canvas,
                          borderRadius: isMobileRoute ? 0 : 1,
                          p: 2.5,
                        }}
                      >
                        {section.title ? (
                          <MDTypography
                            variant="h6"
                            sx={{ color: BRAND.ink, fontWeight: 900, mb: 1 }}
                          >
                            {section.title}
                          </MDTypography>
                        ) : null}
                        {section.body ? (
                          <MDTypography
                            variant="body2"
                            sx={{ color: BRAND.muted, lineHeight: 1.75 }}
                            dangerouslySetInnerHTML={{ __html: section.body }}
                          />
                        ) : null}
                        {section.images?.length > 0 ? (
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            {section.images.map((img, imageIndex) => {
                              const url = mediaUrl(img);
                              return (
                                <Grid
                                  item
                                  xs={6}
                                  sm={4}
                                  md={3}
                                  key={`${index}-${imageIndex}`}
                                >
                                  <Box
                                    component="img"
                                    src={url}
                                    alt={section.title || "Product detail"}
                                    sx={{
                                      width: "100%",
                                      borderRadius: 0.75,
                                      objectFit: "cover",
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        ) : null}
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ) : null}
        </Container>

        {isMobileRoute && hasEffectivePrice && canOrder ? (
          <Box
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
              bgcolor: "#fff",
              borderTop: `1px solid ${BRAND.line}`,
              boxShadow: "none",
              px: 2,
              pt: 1.25,
              pb: "calc(10px + env(safe-area-inset-bottom))",
            }}
          >
            <Container maxWidth="sm" sx={{ px: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <MDBox sx={{ minWidth: 0, flex: 1 }}>
                  <MDTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: BRAND.muted,
                      fontWeight: 800,
                      lineHeight: 1.1,
                    }}
                  >
                    {orderQuantity} item{orderQuantity === 1 ? "" : "s"} total
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    sx={{ color: BRAND.ink, fontWeight: 900, lineHeight: 1.2 }}
                  >
                    {priceText(lineTotal, currency)}
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="outlined"
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    p: 0,
                    borderRadius: 1,
                    color: BRAND.ink,
                    borderColor: "rgba(17,24,39,0.16)",
                  }}
                  onClick={() => navigate(toPath("/cart"))}
                >
                  <Icon>shopping_cart</Icon>
                </MDButton>
                <MDButton
                  variant="contained"
                  sx={{
                    bgcolor: BRAND.red,
                    color: "#fff",
                    minWidth: 132,
                    height: 44,
                    borderRadius: 1,
                    fontWeight: 900,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
                  }}
                  onClick={() => addProductToCart(false)}
                >
                  Add
                </MDButton>
              </Stack>
            </Container>
          </Box>
        ) : null}

        <MDBox
          sx={{
            bgcolor: BRAND.ink,
            color: "#fff",
            py: 4,
            mt: 4,
            display: isMobileRoute ? "none" : "block",
          }}
        >
          <Container maxWidth="lg">
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <MDTypography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.72)" }}
              >
                © {new Date().getFullYear()}{" "}
                {t("common.brandFallback", "Storefront")}
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="text"
                  sx={{ color: "#fff", fontWeight: 900 }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  {t("common.catalog", "Catalog")}
                </MDButton>
                <MDButton
                  variant="text"
                  sx={{ color: "#fff", fontWeight: 900 }}
                  onClick={() => navigate(toPath("/"))}
                >
                  Home
                </MDButton>
              </MDBox>
            </MDBox>
          </Container>
        </MDBox>
      </Box>
    </StorefrontLayout>
  );
}
